import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Modal from '../../components/Modal';
import { apiFetch } from '../../lib/api';
import BACKEND_URL from '../../config.js';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { Sede, SmtpConfig } from '../../types';
import { getRuoloLabel } from '../../utils/ruoliLabel';
import '../../styles/impostazioni.css';

/** Carica immagine logo da GCS proxy e restituisce un blob URL */
async function fetchLogoBlobUrl(sedeId: number, tipo: 'clienti' | 'altre'): Promise<string | null> {
  try {
    const token = localStorage.getItem('token');
    const resp = await fetch(`${BACKEND_URL}/api/sedi/${sedeId}/loghi/${tipo}/file?t=${Date.now()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

// ── tipi ──────────────────────────────────────────────────────────────────────

interface Banca { id: number; nome: string; iban: string | null; }

interface Deposito {
  id: number;
  nome: string;
  tipo: 'FISSO' | 'CONTO_TERZI' | 'COMMESSA';
  attivo: boolean;
  societaId: number;
}

interface SezionaleIva {
  id: number;
  codice: string;
  descrizione: string;
  attivo: boolean;
}

const TIPI_DOCUMENTO = [
  'COMMESSA', 'PREVENTIVO', 'ORDINE_CLIENTE', 'ACCONTO_CLIENTE', 'DISTINTA',
  'SAL_DDT', 'DDT', 'FATTURA', 'FATTURA_DIFFERITA', 'ORDINE_FORNITORE', 'FATTURA_FORNITORE',
] as const;

const LABEL_TIPO: Record<string, string> = {
  COMMESSA: 'Commessa',
  PREVENTIVO: 'Preventivo',
  ORDINE_CLIENTE: 'Ordine cliente',
  ACCONTO_CLIENTE: 'Acconto cliente',
  DISTINTA: 'Distinta',
  SAL_DDT: 'SAL',
  DDT: 'DDT',
  FATTURA: 'Fattura immediata',
  FATTURA_DIFFERITA: 'Fattura differita',
  ORDINE_FORNITORE: 'Ordine fornitore',
  FATTURA_FORNITORE: 'Fattura fornitore',
};

type Tab = 'generale' | 'mail' | 'finanziario' | 'numeratori' | 'loghi';

interface NumeratoreRow {
  tipoDocumento: string;
  prefisso: string;
  ultimoNumero: number;
}

// ── tipo per condizioni ─────────────────────────────────────────────────────
interface CondizioneRiepilogo {
  tipoDocumento: string;
  hasContent: boolean;
  lunghezza: number;
  updatedAt: string | null;
  updatedByName: string | null;
}

interface FormData {
  nome: string;
  indirizzo: string;
  citta: string;
  cap: string;
  provincia: string;
  attiva: boolean;
  societaId: number;
  sedeCommerciale: boolean;
  smtpEmailId: number | null;
  mailMittente: string;
  mailMittenteCcn: boolean;
  mailMittenteNome: string;
  mailMittenteCicloAttivo: string;
  mailMittenteCicloAttivoCcn: boolean;
  mailMittenteNomeCicloAttivo: string;
  mailMittenteCicloPassivo: string;
  mailMittenteCicloPassivoCcn: boolean;
  mailMittenteNomeCicloPassivo: string;
  mailMittenteAmministrazione: string;
  mailMittenteAmministrazioneCcn: boolean;
  mailMittenteNomeAmministrazione: string;
  mailMittenteSicurezza: string;
  mailMittenteSicurezzaCcn: boolean;
  mailMittenteNomeSicurezza: string;
  mailMittenteMagazzino: string;
  mailMittenteMagazzinoCcn: boolean;
  mailMittenteNomeMagazzino: string;
  mailCcn: string[];
  bancaDefaultId: number | null;
  depositoPredefinitoId: number | null;
  sezionaleIvaId: number | null;
  numeratori: NumeratoreRow[];
}

function emptyForm(societaId = 0): FormData {
  return {
    nome: '', indirizzo: '', citta: '', cap: '', provincia: '',
    attiva: true, societaId,
    sedeCommerciale: false,
    smtpEmailId: null, mailMittente: '',
    mailMittenteCcn: false,
    mailMittenteNome: '',
    mailMittenteCicloAttivo: '', mailMittenteCicloAttivoCcn: false,
    mailMittenteNomeCicloAttivo: '',
    mailMittenteCicloPassivo: '', mailMittenteCicloPassivoCcn: false,
    mailMittenteNomeCicloPassivo: '',
    mailMittenteAmministrazione: '', mailMittenteAmministrazioneCcn: false,
    mailMittenteNomeAmministrazione: '',
    mailMittenteSicurezza: '', mailMittenteSicurezzaCcn: false,
    mailMittenteNomeSicurezza: '',
    mailMittenteMagazzino: '', mailMittenteMagazzinoCcn: false,
    mailMittenteNomeMagazzino: '',
    mailCcn: [],
    bancaDefaultId: null,
    depositoPredefinitoId: null,
    sezionaleIvaId: null,
    numeratori: TIPI_DOCUMENTO.map(t => ({ tipoDocumento: t, prefisso: '', ultimoNumero: 0 })),
  };
}

function formatNumeroPreview(prefisso: string, ultimoNumero: number) {
  if (!prefisso) return '—';
  const prossimo = ultimoNumero + 1;
  return `${prefisso}-${new Date().getFullYear()}/${String(prossimo).padStart(3, '0')}`;
}

// ── component ─────────────────────────────────────────────────────────────────

export default function SediPage() {
  const [items, setItems]       = useState<Sede[]>([]);
  const [smtpList, setSmtpList] = useState<SmtpConfig[]>([]);
  const [banche, setBanche]     = useState<Banca[]>([]);
  const [depositi, setDepositi] = useState<Deposito[]>([]);
  const [sezionali, setSezionali] = useState<SezionaleIva[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [modal, setModal]       = useState(false);
  const [form, setForm]         = useState<FormData>(emptyForm());
  const [editId, setEditId]     = useState<number | null>(null);
  const [saving, setSaving]     = useState(false);
  const [tab, setTab]           = useState<Tab>('generale');
  const [expanded, setExpanded] = useState<number | null>(null);
  const [ccnInput, setCcnInput] = useState('');
  const [logoClientiUrl, setLogoClientiUrl]   = useState<string | null>(null);
  const [logoAltreUrl, setLogoAltreUrl]       = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo]     = useState<'clienti' | 'altre' | null>(null);
  const [condizioniRiepilogo, setCondizioniRiepilogo] = useState<CondizioneRiepilogo[]>([]);

  const navigate = useNavigate();

  const {
    societaId: fSocietaIdStr,
    setSocietaId: setFSocietaIdStr,
    listaSocieta: societa,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  const fSocietaId = fSocietaIdStr === '' ? '' : Number(fSocietaIdStr);
  const setFSocietaId = (v: number | '') => setFSocietaIdStr(v === '' ? '' : String(v));

  const itemsFiltrati = useMemo(() =>
    fSocietaId === '' ? items : items.filter(s => s.societaId === fSocietaId),
    [items, fSocietaId]
  );

  const load = async () => {
    if (!pronta) return;
    setLoading(true);
    setError('');
    try {
      const sediRes = await apiFetch<{ sedi: Sede[] }>('/api/sedi');
      setItems(sediRes.sedi ?? []);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }

    // carica risorse opzionali senza bloccare la vista principale
    try {
      const smtpRes = await apiFetch<{ smtp: SmtpConfig[] }>('/api/smtp?tipo=EMAIL');
      setSmtpList(smtpRes.smtp ?? []);
    } catch { /* smtp non critico */ }

    try {
      const bancheQs = fSocietaIdStr ? `?societaId=${fSocietaIdStr}` : '';
      const bancheRes = await apiFetch<Banca[]>(`/api/banche${bancheQs}`);
      setBanche(Array.isArray(bancheRes) ? bancheRes : []);
    } catch { /* banche non critico */ }
  };

  useEffect(() => { load(); }, [pronta]); // eslint-disable-line

  // Carica depositi FISSO attivi della società selezionata nella modale
  useEffect(() => {
    if (!modal || !form.societaId) {
      setDepositi([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // @api GET /api/depositi?societaId=:sid&tipo=FISSO -> { depositi: Deposito[] }
        const res = await apiFetch<{ depositi: Deposito[] }>(
          `/api/depositi?societaId=${form.societaId}&tipo=FISSO`
        );
        if (!cancelled) setDepositi(res.depositi ?? []);
      } catch {
        if (!cancelled) setDepositi([]);
      }
    })();
    return () => { cancelled = true; };
  }, [modal, form.societaId]);

  // Carica i sezionali IVA attivi della società selezionata nella modale
  useEffect(() => {
    if (!modal || !form.societaId) {
      setSezionali([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // @api GET /api/sezionali-iva?societaId=:sid -> { success: true, data: SezionaleIva[] }
        const res = await apiFetch<{ success: boolean; data: SezionaleIva[] }>(
          `/api/sezionali-iva?societaId=${form.societaId}`
        );
        if (!cancelled) setSezionali((res.data ?? []).filter(s => s.attivo));
      } catch {
        if (!cancelled) setSezionali([]);
      }
    })();
    return () => { cancelled = true; };
  }, [modal, form.societaId]);

  // ── modal helpers ────────────────────────────────────────────────────────────

  const openCreate = () => {
    const sid = fSocietaId !== '' ? Number(fSocietaId) : (societa[0]?.id ?? 0);
    setForm(emptyForm(sid));
    setEditId(null); setTab('generale'); setModal(true); setError('');
    setLogoClientiUrl(null);
    setLogoAltreUrl(null);
    setCondizioniRiepilogo([]);
  };

  const openEdit = (s: Sede) => {
    const nums = TIPI_DOCUMENTO.map(t => {
      const existing = s.numeratori?.find(n => n.tipoDocumento === t);
      return { tipoDocumento: t, prefisso: existing?.prefisso ?? '', ultimoNumero: existing?.ultimoNumero ?? 0 };
    });
    setForm({
      nome: s.nome,
      indirizzo: s.indirizzo ?? '',
      citta: s.citta ?? '',
      cap: s.cap ?? '',
      provincia: s.provincia ?? '',
      attiva: s.attiva,
      societaId: s.societaId,
      sedeCommerciale: s.sedeCommerciale ?? false,
      smtpEmailId: s.smtpEmailId ?? null,
      mailMittente: s.mailMittente ?? '',
      mailMittenteCcn: s.mailMittenteCcn ?? false,
      mailMittenteNome: s.mailMittenteNome ?? '',
      mailMittenteCicloAttivo: s.mailMittenteCicloAttivo ?? '',
      mailMittenteCicloAttivoCcn: s.mailMittenteCicloAttivoCcn ?? false,
      mailMittenteNomeCicloAttivo: s.mailMittenteNomeCicloAttivo ?? '',
      mailMittenteCicloPassivo: s.mailMittenteCicloPassivo ?? '',
      mailMittenteCicloPassivoCcn: s.mailMittenteCicloPassivoCcn ?? false,
      mailMittenteNomeCicloPassivo: s.mailMittenteNomeCicloPassivo ?? '',
      mailMittenteAmministrazione: s.mailMittenteAmministrazione ?? '',
      mailMittenteAmministrazioneCcn: s.mailMittenteAmministrazioneCcn ?? false,
      mailMittenteNomeAmministrazione: s.mailMittenteNomeAmministrazione ?? '',
      mailMittenteSicurezza: s.mailMittenteSicurezza ?? '',
      mailMittenteSicurezzaCcn: s.mailMittenteSicurezzaCcn ?? false,
      mailMittenteNomeSicurezza: s.mailMittenteNomeSicurezza ?? '',
      mailMittenteMagazzino: s.mailMittenteMagazzino ?? '',
      mailMittenteMagazzinoCcn: s.mailMittenteMagazzinoCcn ?? false,
      mailMittenteNomeMagazzino: s.mailMittenteNomeMagazzino ?? '',
      mailCcn: s.mailCcn ?? [],
      bancaDefaultId: s.bancaDefaultId ?? null,
      depositoPredefinitoId: s.depositoPredefinitoId ?? null,
      sezionaleIvaId: s.sezionaleIvaId ?? null,
      numeratori: nums,
    });
    setEditId(s.id); setTab('generale'); setModal(true); setError('');
    setLogoClientiUrl(null);
    setLogoAltreUrl(null);
    if (s.logoClientiGcs) {
      fetchLogoBlobUrl(s.id, 'clienti').then(url => { if (url) setLogoClientiUrl(url); });
    }
    if (s.logoAltreStampeGcs) {
      fetchLogoBlobUrl(s.id, 'altre').then(url => { if (url) setLogoAltreUrl(url); });
    }

    // Carica riepilogo condizioni della sede
    setCondizioniRiepilogo([]);
    (async () => {
      try {
        // @api GET /api/sedi/:id/condizioni -> { condizioni: CondizioneRiepilogo[] }
        const res = await apiFetch<{ condizioni: CondizioneRiepilogo[] }>(`/api/sedi/${s.id}/condizioni`);
        setCondizioniRiepilogo(res.condizioni ?? []);
      } catch {
        setCondizioniRiepilogo([]);
      }
    })();
  };

  const closeModal = () => { setModal(false); setError(''); };

  // ── save ─────────────────────────────────────────────────────────────────────

  const save = async () => {
    if (!form.nome.trim() || !form.societaId) { setError('Nome e società sono obbligatori'); return; }
    if (!form.depositoPredefinitoId) {
      setError('Il deposito predefinito è obbligatorio');
      return;
    }
    // Il sezionale IVA è obbligatorio, ma non alla creazione: quando si crea la
    // sede il sezionale spesso non esiste ancora. In modifica lo esigiamo solo
    // se la società ha almeno un sezionale attivo assegnabile.
    if (editId && !form.sezionaleIvaId && sezionali.length > 0) {
      setTab('generale');
      setError('Il sezionale IVA è obbligatorio: seleziona il sezionale su cui confluiscono i documenti fiscali di questa sede.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        nome: form.nome.trim(),
        indirizzo: form.indirizzo || null,
        citta: form.citta || null,
        cap: form.cap || null,
        provincia: form.provincia || null,
        attiva: form.attiva,
        societaId: Number(form.societaId),
        sedeCommerciale: form.sedeCommerciale,
        smtpEmailId: form.smtpEmailId || null,
        mailMittente: form.mailMittente || null,
        mailMittenteCcn: form.mailMittenteCcn,
        mailMittenteNome: form.mailMittenteNome || null,
        mailMittenteCicloAttivo: form.mailMittenteCicloAttivo || null,
        mailMittenteCicloAttivoCcn: form.mailMittenteCicloAttivoCcn,
        mailMittenteNomeCicloAttivo: form.mailMittenteNomeCicloAttivo || null,
        mailMittenteCicloPassivo: form.mailMittenteCicloPassivo || null,
        mailMittenteCicloPassivoCcn: form.mailMittenteCicloPassivoCcn,
        mailMittenteNomeCicloPassivo: form.mailMittenteNomeCicloPassivo || null,
        mailMittenteAmministrazione: form.mailMittenteAmministrazione || null,
        mailMittenteAmministrazioneCcn: form.mailMittenteAmministrazioneCcn,
        mailMittenteNomeAmministrazione: form.mailMittenteNomeAmministrazione || null,
        mailMittenteSicurezza: form.mailMittenteSicurezza || null,
        mailMittenteSicurezzaCcn: form.mailMittenteSicurezzaCcn,
        mailMittenteNomeSicurezza: form.mailMittenteNomeSicurezza || null,
        mailMittenteMagazzino: form.mailMittenteMagazzino || null,
        mailMittenteMagazzinoCcn: form.mailMittenteMagazzinoCcn,
        mailMittenteNomeMagazzino: form.mailMittenteNomeMagazzino || null,
        mailCcn: form.mailCcn,
        bancaDefaultId: form.bancaDefaultId || null,
        depositoPredefinitoId: form.depositoPredefinitoId || null,
        sezionaleIvaId: form.sezionaleIvaId || null,
      };

      let id = editId;
      if (editId) {
        await apiFetch(`/api/sedi/${editId}`, { method: 'PUT', body: JSON.stringify(body) });
      } else {
        const res = await apiFetch<{ sede: Sede }>('/api/sedi', { method: 'POST', body: JSON.stringify(body) });
        id = res.sede.id;
      }

      // save numeratori
      const numPayload = form.numeratori.filter(n => n.prefisso.trim());
      if (numPayload.length && id) {
        await apiFetch(`/api/sedi/${id}/numeratori`, {
          method: 'PUT',
          body: JSON.stringify({ numeratori: numPayload.map(n => ({ tipoDocumento: n.tipoDocumento, prefisso: n.prefisso.trim() })) }),
        });
      }

      closeModal(); load();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Eliminare questa sede?')) return;
    try { await apiFetch(`/api/sedi/${id}`, { method: 'DELETE' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  // ── CCN helpers ──────────────────────────────────────────────────────────────

  const addCcn = () => {
    const v = ccnInput.trim();
    if (!v || form.mailCcn.includes(v)) return;
    setForm(f => ({ ...f, mailCcn: [...f.mailCcn, v] }));
    setCcnInput('');
  };
  const removeCcn = (email: string) =>
    setForm(f => ({ ...f, mailCcn: f.mailCcn.filter(x => x !== email) }));

  // ── numeratori helper ────────────────────────────────────────────────────────

  const setNumeratorePrefisso = (tipo: string, prefisso: string) =>
    setForm(f => ({ ...f, numeratori: f.numeratori.map(n => n.tipoDocumento === tipo ? { ...n, prefisso } : n) }));

  const uploadLogo = async (tipoLogo: 'clienti' | 'altre', file: File) => {
    if (!editId) return;
    setUploadingLogo(tipoLogo);
    try {
      const fd = new FormData();
      fd.append('tipoLogo', tipoLogo);
      fd.append('file', file);
      // @api POST /api/sedi/:id/loghi -> { success: true, gcsPath: string }
      const res = await apiFetch<{ success: boolean; gcsPath: string }>(
        `/api/sedi/${editId}/loghi`,
        { method: 'POST', body: fd }
      );
      if (res.success) {
        const blobUrl = await fetchLogoBlobUrl(editId, tipoLogo);
        if (tipoLogo === 'clienti') setLogoClientiUrl(blobUrl);
        else setLogoAltreUrl(blobUrl);
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setUploadingLogo(null);
    }
  };

  const removeLogo = async (tipoLogo: 'clienti' | 'altre') => {
    if (!editId || !window.confirm('Eliminare questo logo?')) return;
    try {
      // @api DELETE /api/sedi/:id/loghi/:tipoLogo -> { success: true }
      await apiFetch(`/api/sedi/${editId}/loghi/${tipoLogo}`, { method: 'DELETE' });
      if (tipoLogo === 'clienti') setLogoClientiUrl(null);
      else setLogoAltreUrl(null);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const bancaSelezionata = (banche ?? []).find(b => b.id === form.bancaDefaultId) ?? null;

  // ── render ────────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h1 className="bop-page-title">Sedi</h1>
          <p className="bop-text-muted">Sedi operative, configurazione mail e numeratori documenti</p>
        </div>
        <button className="bop-btn bop-btn-primary" onClick={openCreate}>+ Nuova sede</button>
      </div>

      {error && !modal && <div className="bop-inline-error">{error}</div>}

      {!isUnica && (
        <div className="bop-filter-bar">
          <select
            className="bop-select impostazioni-sedi-select-societa"
            value={fSocietaId}
            onChange={e => setFSocietaId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">Tutte le società</option>
            {societa.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        </div>
      )}

      <div className="bop-card">
        <div className="bop-table-wrapper">
          {loading ? (
            <div className="inline-loading">Caricamento…</div>
          ) : itemsFiltrati.length === 0 ? (
            <div className="bop-empty-state">{items.length === 0 ? 'Nessuna sede registrata.' : 'Nessuna sede per la società selezionata.'}</div>
          ) : (
            <table className="bop-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Città</th>
                  <th>Società</th>
                  <th>Sezionale IVA</th>
                  <th>Config</th>
                  <th>Utenti</th>
                  <th>Stato</th>
                  <th className="bop-th-actions--sm"></th>
                </tr>
              </thead>
              <tbody>
                {itemsFiltrati.map((sede) => (
                  <React.Fragment key={sede.id}>
                    <tr>
                      <td>
                        <strong>{sede.nome}</strong>
                        {sede.sedeCommerciale && (
                          <span className="impostazioni-sedi-badge-commerciale">Commerciale</span>
                        )}
                      </td>
                      <td className="impostazioni-text-muted">
                        {sede.citta ? `${sede.citta}${sede.provincia ? ` (${sede.provincia})` : ''}` : '—'}
                      </td>
                      <td className="impostazioni-text-muted">{sede.societaNome ?? sede.societaId}</td>
                      <td>
                        {sede.sezionaleIvaId ? (
                          <span title={sede.sezionaleIvaDescrizione ?? ''}>
                            <strong>{sede.sezionaleIvaCodice}</strong>
                            {sede.sezionaleIvaDescrizione && (
                              <span className="impostazioni-text-muted"> — {sede.sezionaleIvaDescrizione}</span>
                            )}
                          </span>
                        ) : sede.attiva ? (
                          <span
                            className="bop-badge bop-badge-warning"
                            title="Ogni sede deve confluire in un sezionale IVA per poter stampare il registro IVA di sede. Assegnalo in modifica."
                          >
                            Da assegnare
                          </span>
                        ) : (
                          <span className="impostazioni-text-empty">—</span>
                        )}
                      </td>
                      <td>
                        {sede.smtpEmailId && (
                          <span title={`SMTP: ${sede.smtpNome ?? sede.smtpEmailId}`} className="impostazioni-sedi-icon">✉️</span>
                        )}
                        {sede.bancaDefaultId && (
                          <span title={`Banca: ${sede.bancaNome ?? sede.bancaDefaultId}`} className="impostazioni-sedi-icon">🏦</span>
                        )}
                        {sede.depositoPredefinitoId && (
                          <span title={`Deposito: ${sede.depositoPredefinitoNome ?? sede.depositoPredefinitoId}`} className="impostazioni-sedi-icon">📦</span>
                        )}
                      </td>
                      <td>
                        {(sede.utenti?.length ?? 0) > 0 ? (
                          <button
                            className="impostazioni-sedi-btn-toggle"
                            onClick={() => setExpanded(expanded === sede.id ? null : sede.id)}
                          >
                            {sede.utenti!.length} utent{sede.utenti!.length === 1 ? 'e' : 'i'}{' '}
                            {expanded === sede.id ? '▲' : '▼'}
                          </button>
                        ) : (
                          <span
                            className="impostazioni-text-empty"
                            title="Nessun utente associato a questa sede. Vai in Gestione Utenti per associarli."
                          >—</span>
                        )}
                      </td>
                      <td>
                        <span className={`bop-badge ${sede.attiva ? 'bop-badge-success' : 'bop-badge-danger'}`}>
                          {sede.attiva ? 'Attiva' : 'Disattiva'}
                        </span>
                      </td>
                      <td>
                        <div className="td-actions">
                          <button className="bop-btn bop-btn-ghost" onClick={() => openEdit(sede)}>Modifica</button>
                          <button className="bop-btn bop-btn-ghost bop-text-danger" onClick={() => remove(sede.id)}>Elimina</button>
                        </div>
                      </td>
                    </tr>
                    {expanded === sede.id && (
                      <tr>
                        <td colSpan={8} className="impostazioni-sedi-utenti-row">
                          <div className="impostazioni-sedi-utenti-list">
                            {sede.utenti?.map(u => (
                              <span key={u.id} className="impostazioni-sedi-utente-chip" title={u.email}>
                                {u.nome} <span className="impostazioni-text-muted">({getRuoloLabel(u.ruolo)})</span>
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Modal ── */}
      {modal && (
        <Modal
          title={editId ? 'Modifica sede' : 'Nuova sede'}
          onClose={closeModal}
          size="lg"
          footer={
            <>
              <button className="bop-btn bop-btn-secondary" onClick={closeModal}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Salvo…' : 'Salva'}
              </button>
            </>
          }
        >
          {error && <div className="bop-inline-error">{error}</div>}

          {/* Tab bar */}
          <div className="bop-tabs">
            {(['generale', 'mail', 'finanziario', 'numeratori', 'loghi'] as Tab[]).map(t => (
              <button key={t} className={`bop-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
                {{ generale: 'Dati generali', mail: 'Config mail', finanziario: 'Finanziario', numeratori: 'Numeratori e condizioni', loghi: 'Loghi e immagini' }[t]}
              </button>
            ))}
          </div>

          {/* ── Tab: Generale ── */}
              {tab === 'generale' && (
                <>
                  <div className="impostazioni-sedi-form-row">
                    <div className="bop-form-group impostazioni-sedi-form-half">
                      <label>Nome <span className="req">*</span></label>
                      <input className="bop-input" value={form.nome}
                        onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} />
                    </div>
                    <div className="bop-form-group impostazioni-sedi-form-half">
                      <label>Società <span className="req">*</span></label>
                      <select className="bop-select" value={form.societaId}
                        onChange={e => {
                          const newSocId = Number(e.target.value);
                          setForm(f => ({
                            ...f,
                            societaId: newSocId,
                            depositoPredefinitoId: f.societaId === newSocId ? f.depositoPredefinitoId : null,
                            sezionaleIvaId: f.societaId === newSocId ? f.sezionaleIvaId : null,
                          }));
                        }}>
                        <option value={0}>Seleziona…</option>
                        {societa.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="bop-form-group">
                    <label>Indirizzo</label>
                    <input className="bop-input" value={form.indirizzo}
                      onChange={e => setForm(f => ({ ...f, indirizzo: e.target.value }))} />
                  </div>
                  <div className="impostazioni-sedi-form-row">
                    <div className="bop-form-group impostazioni-sedi-form-half">
                      <label>Città</label>
                      <input className="bop-input" value={form.citta}
                        onChange={e => setForm(f => ({ ...f, citta: e.target.value }))} />
                    </div>
                    <div className="bop-form-group impostazioni-sedi-form-cap">
                      <label>CAP</label>
                      <input className="bop-input" maxLength={5} value={form.cap}
                        onChange={e => setForm(f => ({ ...f, cap: e.target.value }))} />
                    </div>
                    <div className="bop-form-group impostazioni-sedi-form-prov">
                      <label>Prov.</label>
                      <input className="bop-input" maxLength={2} value={form.provincia}
                        onChange={e => setForm(f => ({ ...f, provincia: e.target.value.toUpperCase() }))} placeholder="MI" />
                    </div>
                  </div>
                  <div className="bop-form-group">
                    <label>Deposito predefinito <span className="req">*</span></label>
                    <select
                      className="bop-select"
                      value={form.depositoPredefinitoId ?? ''}
                      onChange={e => setForm(f => ({
                        ...f,
                        depositoPredefinitoId: e.target.value ? Number(e.target.value) : null,
                      }))}
                      disabled={!form.societaId}
                    >
                      <option value="">
                        {!form.societaId
                          ? 'Seleziona prima la società'
                          : depositi.length === 0
                            ? 'Nessun deposito FISSO per questa società'
                            : 'Seleziona…'}
                      </option>
                      {depositi.map(d => (
                        <option key={d.id} value={d.id}>{d.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div className="bop-form-group">
                    <label>Sezionale IVA <span className="req">*</span></label>
                    <select
                      className="bop-select"
                      value={form.sezionaleIvaId ?? ''}
                      onChange={e => setForm(f => ({
                        ...f,
                        sezionaleIvaId: e.target.value ? Number(e.target.value) : null,
                      }))}
                      disabled={!form.societaId}
                    >
                      <option value="">
                        {!form.societaId
                          ? 'Seleziona prima la società'
                          : sezionali.length === 0
                            ? 'Nessun sezionale IVA attivo per questa società'
                            : 'Seleziona…'}
                      </option>
                      {sezionali.map(s => (
                        <option key={s.id} value={s.id}>{s.codice} — {s.descrizione}</option>
                      ))}
                    </select>
                    <small className="impostazioni-text-muted">
                      I documenti fiscali emessi dalla sede confluiscono in questo sezionale,
                      così da poter stampare il registro IVA della singola sede.
                      {sezionali.length === 0
                        ? ' Puoi salvare la sede e collegarlo più avanti, dopo aver creato i sezionali in Amministrazione → Sezionali IVA.'
                        : ''}
                    </small>
                  </div>
                  <div className="bop-form-group">
                    <label className="form-check">
                      <input type="checkbox" checked={form.attiva}
                        onChange={e => setForm(f => ({ ...f, attiva: e.target.checked }))} />
                      Attiva
                    </label>
                  </div>
                  <div className="bop-form-group">
                    <label className="form-check">
                      <input type="checkbox" checked={form.sedeCommerciale}
                        onChange={e => setForm(f => ({ ...f, sedeCommerciale: e.target.checked }))} />
                      Sede commerciale
                    </label>
                  </div>
                </>
              )}

              {/* ── Tab: Mail ── */}
              {tab === 'mail' && (
                <>
                  <div className="bop-form-group">
                    <label>Configurazione SMTP</label>
                    <select className="bop-select" value={form.smtpEmailId ?? ''}
                      onChange={e => setForm(f => ({ ...f, smtpEmailId: e.target.value ? Number(e.target.value) : null }))}>
                      <option value="">Nessuna (usa default società)</option>
                      {(smtpList ?? []).map(s => (
                        <option key={s.id} value={s.id}>{s.fromName} &lt;{s.fromEmail}&gt;</option>
                      ))}
                    </select>
                  </div>

                  <div className="impostazioni-sedi-mittenti-section">
                    <div className="impostazioni-sedi-mittenti-header">
                      <span className="impostazioni-sedi-mittenti-col-label">Ambito</span>
                      <span className="impostazioni-sedi-mittenti-col-email">Indirizzo mittente</span>
                      <span className="impostazioni-sedi-mittenti-col-nome">Nome mittente</span>
                      <span className="impostazioni-sedi-mittenti-col-ccn" title="Aggiungi sempre il mittente in CCN">CCN self</span>
                    </div>

                    {([
                      { key: 'mailMittente',              nomeKey: 'mailMittenteNome',              ccnKey: 'mailMittenteCcn',               label: 'Generico',        hint: "Se vuoto, usa l'indirizzo della configurazione SMTP" },
                      { key: 'mailMittenteCicloAttivo',   nomeKey: 'mailMittenteNomeCicloAttivo',   ccnKey: 'mailMittenteCicloAttivoCcn',    label: 'Ciclo attivo',    hint: null },
                      { key: 'mailMittenteCicloPassivo',  nomeKey: 'mailMittenteNomeCicloPassivo',  ccnKey: 'mailMittenteCicloPassivoCcn',   label: 'Ciclo passivo',   hint: null },
                      { key: 'mailMittenteAmministrazione', nomeKey: 'mailMittenteNomeAmministrazione', ccnKey: 'mailMittenteAmministrazioneCcn', label: 'Amministrazione', hint: null },
                      { key: 'mailMittenteSicurezza',     nomeKey: 'mailMittenteNomeSicurezza',     ccnKey: 'mailMittenteSicurezzaCcn',      label: 'Sicurezza',       hint: null },
                      { key: 'mailMittenteMagazzino',     nomeKey: 'mailMittenteNomeMagazzino',     ccnKey: 'mailMittenteMagazzinoCcn',      label: 'Magazzino',       hint: null },
                    ] as Array<{ key: keyof FormData; nomeKey: keyof FormData; ccnKey: keyof FormData; label: string; hint: string | null }>).map(({ key, nomeKey, ccnKey, label, hint }) => (
                      <div key={key} className="impostazioni-sedi-mittente-row">
                        <span className="impostazioni-sedi-mittenti-col-label impostazioni-sedi-mittente-label">{label}</span>
                        <div className="impostazioni-sedi-mittenti-col-email">
                          <input
                            className="bop-input"
                            type="email"
                            value={form[key] as string}
                            placeholder="indirizzo@example.com"
                            onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                          />
                          {hint && <small className="impostazioni-text-muted">{hint}</small>}
                        </div>
                        <div className="impostazioni-sedi-mittenti-col-nome">
                          <input
                            className="bop-input"
                            type="text"
                            value={form[nomeKey] as string}
                            placeholder="Es. Ufficio Vendite"
                            onChange={e => setForm(f => ({ ...f, [nomeKey]: e.target.value }))}
                          />
                        </div>
                        <div className="impostazioni-sedi-mittenti-col-ccn">
                          <label className="impostazioni-sedi-ccn-self-toggle">
                            <input
                              type="checkbox"
                              checked={form[ccnKey] as boolean}
                              onChange={e => setForm(f => ({ ...f, [ccnKey]: e.target.checked }))}
                            />
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bop-form-group impostazioni-sedi-ccn-global">
                    <label>Indirizzi CCN aggiuntivi</label>
                    <div className="impostazioni-sedi-ccn-input-row">
                      <input className="bop-input" type="email" value={ccnInput}
                        placeholder="email@example.com"
                        onChange={e => setCcnInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCcn())} />
                      <button className="btn btn-secondary" type="button" onClick={addCcn}>Aggiungi</button>
                    </div>
                    <div className="impostazioni-sedi-ccn-list">
                      {(form.mailCcn ?? []).map(email => (
                        <span key={email} className="impostazioni-sedi-ccn-tag">
                          {email}
                          <button className="impostazioni-sedi-ccn-remove"
                            onClick={() => removeCcn(email)}>×</button>
                        </span>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* ── Tab: Finanziario ── */}
              {tab === 'finanziario' && (
                <>
                  <div className="bop-form-group">
                    <label>Banca predefinita</label>
                    <select className="bop-select" value={form.bancaDefaultId ?? ''}
                      onChange={e => setForm(f => ({ ...f, bancaDefaultId: e.target.value ? Number(e.target.value) : null }))}>
                      <option value="">Nessuna</option>
                      {(banche ?? []).map(b => <option key={b.id} value={b.id}>{b.nome}</option>)}
                    </select>
                  </div>
                  {bancaSelezionata && (
                    <div className="impostazioni-sedi-banca-info">
                      <div className="impostazioni-sedi-banca-label">IBAN</div>
                      <div className="impostazioni-sedi-banca-iban">
                        {bancaSelezionata.iban || '—'}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* ── Tab: Numeratori e condizioni ── */}
              {tab === 'numeratori' && (
                <>
                  <p className="impostazioni-sedi-numeratori-help">
                    Definisci il prefisso e le condizioni generali per ogni tipo di documento.
                    Il numero progressivo viene incrementato automaticamente.
                  </p>
                  <table className="impostazioni-sedi-numeratori-table">
                    <thead>
                      <tr>
                        <th>Tipo documento</th>
                        <th className="impostazioni-sedi-numeratori-th-prefisso">Prefisso</th>
                        <th>Prossimo numero</th>
                        <th>Condizioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(form.numeratori ?? []).map(n => {
                        const cond = condizioniRiepilogo.find(c => c.tipoDocumento === n.tipoDocumento);
                        const hasContent = cond?.hasContent ?? false;
                        const len = cond?.lunghezza ?? 0;
                        return (
                          <tr key={n.tipoDocumento}>
                            <td>{LABEL_TIPO[n.tipoDocumento]}</td>
                            <td className="impostazioni-sedi-numeratori-cell-prefisso">
                              <input
                                className="impostazioni-sedi-numeratori-input impostazioni-sedi-numeratori-input--small"
                                value={n.prefisso}
                                placeholder="es. MIL"
                                maxLength={10}
                                onChange={e => setNumeratorePrefisso(n.tipoDocumento, e.target.value.toUpperCase())}
                              />
                            </td>
                            <td className={`impostazioni-sedi-numeratori-preview ${n.prefisso ? '' : 'impostazioni-text-muted'}`}>
                              {formatNumeroPreview(n.prefisso, n.ultimoNumero)}
                            </td>
                            <td className="impostazioni-sedi-condizioni-cell">
                              {hasContent && (
                                <span className="bop-badge bop-badge-success impostazioni-sedi-condizioni-badge">
                                  ✓ Definite ({len.toLocaleString('it-IT')} car.)
                                </span>
                              )}
                              <button
                                type="button"
                                className="bop-btn bop-btn-ghost impostazioni-sedi-condizioni-btn"
                                disabled={!editId}
                                title={!editId ? 'Salva prima la sede' : 'Modifica condizioni generali'}
                                onClick={() => {
                                  if (!editId) return;
                                  window.open(`/impostazioni/sedi/${editId}/condizioni/${n.tipoDocumento}`, '_blank');
                                }}
                              >
                                Modifica
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              )}

              {/* ── Tab: Loghi e immagini ── */}
              {tab === 'loghi' && (
                <div className="impostazioni-sedi-loghi-tab">
                  {!editId && (
                    <p className="impostazioni-sedi-loghi-avviso">
                      Salva prima la sede per poter caricare i loghi.
                    </p>
                  )}

                  {/* Slot 1: Logo stampe clienti/fornitori */}
                  <div className="impostazioni-sedi-logo-slot">
                    <div className="impostazioni-sedi-logo-slot__header">
                      <div className="impostazioni-sedi-logo-slot__title">Logo stampe clienti e fornitori</div>
                      <div className="impostazioni-sedi-logo-slot__desc bop-text-muted">
                        Utilizzato nelle stampe di preventivi, ordini, fatture e DDT.
                      </div>
                    </div>
                    <div className="impostazioni-sedi-logo-slot__body">
                      {logoClientiUrl ? (
                        <div className="impostazioni-sedi-logo-preview">
                          <img src={logoClientiUrl} alt="Logo clienti" className="impostazioni-sedi-logo-img" />
                          {editId && (
                            <button
                              className="bop-btn bop-btn-ghost bop-text-danger impostazioni-sedi-logo-remove"
                              onClick={() => removeLogo('clienti')}
                            >
                              Rimuovi
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="impostazioni-sedi-logo-empty">Nessun logo caricato</div>
                      )}
                      {editId && (
                        <label className="bop-btn bop-btn-secondary impostazioni-sedi-logo-upload-btn">
                          {uploadingLogo === 'clienti' ? 'Caricamento…' : logoClientiUrl ? 'Sostituisci' : 'Carica logo'}
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                            className="impostazioni-sedi-logo-input"
                            disabled={uploadingLogo !== null}
                            onChange={e => {
                              const f = e.target.files?.[0];
                              if (f) uploadLogo('clienti', f);
                              e.target.value = '';
                            }}
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Slot 2: Logo altre stampe */}
                  <div className="impostazioni-sedi-logo-slot">
                    <div className="impostazioni-sedi-logo-slot__header">
                      <div className="impostazioni-sedi-logo-slot__title">Logo altre stampe</div>
                      <div className="impostazioni-sedi-logo-slot__desc bop-text-muted">
                        Utilizzato nei report interni, riepiloghi e documenti non commerciali.
                      </div>
                    </div>
                    <div className="impostazioni-sedi-logo-slot__body">
                      {logoAltreUrl ? (
                        <div className="impostazioni-sedi-logo-preview">
                          <img src={logoAltreUrl} alt="Logo altre stampe" className="impostazioni-sedi-logo-img" />
                          {editId && (
                            <button
                              className="bop-btn bop-btn-ghost bop-text-danger impostazioni-sedi-logo-remove"
                              onClick={() => removeLogo('altre')}
                            >
                              Rimuovi
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="impostazioni-sedi-logo-empty">Nessun logo caricato</div>
                      )}
                      {editId && (
                        <label className="bop-btn bop-btn-secondary impostazioni-sedi-logo-upload-btn">
                          {uploadingLogo === 'altre' ? 'Caricamento…' : logoAltreUrl ? 'Sostituisci' : 'Carica logo'}
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                            className="impostazioni-sedi-logo-input"
                            disabled={uploadingLogo !== null}
                            onChange={e => {
                              const f = e.target.files?.[0];
                              if (f) uploadLogo('altre', f);
                              e.target.value = '';
                            }}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              )}
        </Modal>
      )}
    </Layout>
  );
}
